import React, { useEffect, useState, useRef } from "react";
// Import QRCodeSVG from the approved qrcode.react library to draw the vector QR code
import { QRCodeSVG } from "qrcode.react";
// Import Convex useAction hook
import { useAction } from "convex/react";
// Import Convex API schema definitions
import { api } from "../../../convex/_generated/api";

/**
 * Prop interface for the MobilePayQR component.
 * - subscriptionId: The unique Stripe subscription ID we want to generate a pay token for.
 */
export interface MobilePayQRProps {
  subscriptionId: string;
}

/**
 * MobilePayQR Component
 * Displays a desktop QR code that redirects to the mobile checkout view.
 * Uses an opaque single-use payToken to avoid leaking Stripe client_secret to QR code, URL, database, or logs (Security H2).
 */
export const MobilePayQR: React.FC<MobilePayQRProps> = ({ subscriptionId }) => {
  // Hook to create the handshake payment token
  const createPayToken = useAction(api.stripe.createPayToken);

  // Component state variables
  const [payToken, setPayToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Keep track of mounting to prevent duplicate action invocations
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!subscriptionId) return;

    // Use a unique fetch key incorporating the retry count to lock the query
    const fetchKey = `${subscriptionId}_${retryCount}`;
    if (fetchedRef.current === fetchKey) return;

    fetchedRef.current = fetchKey;

    const generateToken = async () => {
      setLoading(true);
      setError(null);
      setPayToken("");

      try {
        const result = await createPayToken({ subscriptionId });
        setPayToken(result.payToken);
      } catch (err) {
        // Unlock on error to allow user-driven retries
        fetchedRef.current = null;
        setError(
          err instanceof Error
            ? err.message
            : "Failed to generate mobile payment QR code."
        );
      } finally {
        setLoading(false);
      }
    };

    generateToken();
  }, [subscriptionId, createPayToken, retryCount]);

  // Loading indicator matching the theme
  if (loading) {
    return (
      <div
        className="qr-loading-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          backgroundColor: "#161D30",
          borderRadius: "var(--border-radius-md)",
          minHeight: "240px",
        }}
        data-testid="qr-loading"
      >
        <div className="loading-spinner" style={{ borderTopColor: "#00D4FF", width: "32px", height: "32px", border: "4px solid rgba(255,255,255,0.1)", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <p style={{ marginTop: "1rem", color: "var(--color-text-secondary)", fontSize: "0.85rem" }}>
          Generating secure token...
        </p>
      </div>
    );
  }

  // Error card displaying problems with token generation
  if (error) {
    return (
      <div
        className="qr-error-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          backgroundColor: "#161D30",
          borderRadius: "var(--border-radius-md)",
          textAlign: "center",
          minHeight: "240px",
        }}
        data-testid="qr-error"
      >
        <p style={{ color: "#FF0055", fontSize: "0.9rem", fontWeight: "600", marginBottom: "1rem" }}>
          {error}
        </p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setError(null);
            setPayToken("");
            setRetryCount((prev) => prev + 1);
          }}
          style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem" }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Idle state if subscription ID was not provided or token is empty
  if (!payToken) {
    return null;
  }

  // Opaque mobile pay url proxy (Enforces Security Rule: NO client_secret in QR value)
  const mobilePayUrl = `${window.location.origin}/mobile-pay?t=${payToken}`;

  return (
    <div
      className="mobile-pay-qr-card"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1.5rem",
        backgroundColor: "#161D30",
        borderRadius: "var(--border-radius-md)",
        boxShadow: "0 0 20px rgba(0, 212, 255, 0.1)",
        maxWidth: "280px",
        margin: "0 auto",
      }}
      data-testid="mobile-pay-qr"
    >
      <h4 style={{ color: "#FFFFFF", marginBottom: "0.5rem", fontSize: "1rem", fontWeight: "600" }}>
        Pay via Apple / Google Pay
      </h4>
      <p style={{ color: "var(--color-text-secondary)", fontSize: "0.8rem", textAlign: "center", marginBottom: "1.25rem" }}>
        Scan the QR code with your phone to complete your upgrade securely.
      </p>

      {/* High-contrast frame wrapper surrounding the SVG vector QR block */}
      <div
        className="qr-frame"
        style={{
          padding: "12px",
          backgroundColor: "#FFFFFF", // High contrast white background for reliable scanner recognition
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
        data-testid="qr-frame"
      >
        <QRCodeSVG
          value={mobilePayUrl}
          size={192}
          level="H" // High error correction level for easy camera scanning
          includeMargin={false}
        />
      </div>

      <div style={{ marginTop: "1rem", fontSize: "0.75rem", color: "#00D4FF", fontWeight: "600" }}>
        Token Expires in 10m
      </div>
    </div>
  );
};
